/**
 * AdminOnlineApplications — dashboard for applicants marked with the
 * internal `online_lead` checkbox. Keeps the data model simple: one boolean
 * flag on the application; UI aggregates counts + funded totals + a
 * 12-month trend from existing loan/application records.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Loader2, Globe, TrendingUp, Download } from "lucide-react";

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "last30", label: "Last 30 days" },
  { key: "ytd", label: "YTD" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

function _rangeFor(key) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(now);
  if (key === "today") return { start: iso(now), end: iso(now) };
  if (key === "week")   { start.setDate(now.getDate() - now.getDay()); return { start: iso(start), end: iso(now) }; }
  if (key === "month")  { start.setDate(1); return { start: iso(start), end: iso(now) }; }
  if (key === "last30") { start.setDate(now.getDate() - 30); return { start: iso(start), end: iso(now) }; }
  if (key === "ytd")    { return { start: `${now.getFullYear()}-01-01`, end: iso(now) }; }
  return { start: null, end: null };  // all-time / custom (custom uses caller state)
}

const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

const STATUS_COLORS = {
  new:              "bg-blue-50 text-blue-800 border-blue-200",
  under_review:     "bg-amber-50 text-amber-800 border-amber-200",
  waiting_attorney: "bg-purple-50 text-purple-800 border-purple-200",
  approved:         "bg-emerald-50 text-emerald-800 border-emerald-200",
  declined:         "bg-rose-50 text-rose-800 border-rose-200",
  funded:           "bg-teal-50 text-teal-800 border-teal-200",
};

export default function AdminOnlineApplications() {
  const [presetKey, setPresetKey] = useState("last30");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [stateFilter, setStateFilter] = useState("");   // "" = All states
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeRange = presetKey === "custom"
    ? { start: customStart || null, end: customEnd || null }
    : _rangeFor(presetKey);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeRange.start) params.start = activeRange.start;
      if (activeRange.end)   params.end   = activeRange.end;
      if (stateFilter)       params.state = stateFilter;
      const { data } = await api.get("/online-applications/dashboard", { params });
      setData(data);
    } catch (e) {
      toast.error("Load failed: " + formatApiError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey, customStart, customEnd, stateFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = data?.kpis || {};
  const rows = data?.rows || [];
  const trend = data?.trend || [];
  const availableStates = data?.available_states || [];
  const maxTrend = Math.max(1, ...trend.map(t => Math.max(t.applications, t.funded)));

  return (
    <div className="p-6 space-y-5" data-testid="admin-online-applications">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#0066FF]" />
            <h1 className="text-2xl font-semibold text-[#0A2540]">Online Applications</h1>
          </div>
          <p className="text-sm text-[#6B7280] mt-1">
            Applicants marked <b>Online Lead</b> on their application record.
          </p>
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
        {PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => setPresetKey(p.key)}
            data-testid={`oa-preset-${p.key}`}
            className={
              "h-8 px-3 rounded-md text-xs font-medium border " +
              (presetKey === p.key
                ? "bg-[#0066FF] text-white border-[#0066FF]"
                : "bg-white text-[#6B7280] border-[#E5E7EB] hover:bg-[#F9FAFB]")
            }
          >{p.label}</button>
        ))}
        {presetKey === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} data-testid="oa-custom-start" className="h-8 px-2 text-xs border border-[#E5E7EB] rounded-md" />
            <span className="text-xs text-[#6B7280]">to</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-testid="oa-custom-end" className="h-8 px-2 text-xs border border-[#E5E7EB] rounded-md" />
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">State</label>
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            data-testid="oa-state-filter"
            className="h-8 px-2 text-xs border border-[#E5E7EB] rounded-md bg-white text-[#0A2540] min-w-[7rem]"
          >
            <option value="">All states</option>
            {availableStates.map(s => (
              <option key={s} value={s} data-testid={`oa-state-option-${s}`}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3" data-testid="oa-kpis">
        <KPI label="Online applications" value={kpis.online_applications ?? 0} testid="oa-kpi-apps" />
        <KPI label="Funded" value={kpis.funded ?? 0} testid="oa-kpi-funded" />
        <KPI label="Conversion rate" value={fmtPct(kpis.conversion_rate_pct)} testid="oa-kpi-conv" />
        <KPI label="Total funded $" value={fmtUSD(kpis.total_funded_amount)} testid="oa-kpi-total" />
        <KPI label="Avg. funded amount" value={fmtUSD(kpis.avg_funded_amount)} testid="oa-kpi-avg" />
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-white p-5" data-testid="oa-trend">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#0066FF]" />
          <div className="text-sm font-semibold text-[#0A2540]">Monthly trend — last 12 months</div>
        </div>
        <div className="grid grid-cols-12 gap-1.5">
          {trend.map(t => (
            <div key={t.month} className="flex flex-col items-center gap-1" data-testid={`oa-trend-${t.month}`}>
              <div className="h-32 w-full flex items-end justify-center gap-0.5">
                <div title={`${t.applications} applications`}
                     className="w-2.5 bg-[#0066FF] rounded-t-sm"
                     style={{ height: `${(t.applications / maxTrend) * 100}%` }} />
                <div title={`${t.funded} funded`}
                     className="w-2.5 bg-emerald-500 rounded-t-sm"
                     style={{ height: `${(t.funded / maxTrend) * 100}%` }} />
              </div>
              <div className="text-[9px] text-[#6B7280] font-mono">{t.month.slice(5)}</div>
              <div className="text-[9px] text-[#0A2540]">{t.applications}/{t.funded}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 text-[11px] text-[#6B7280] mt-3">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#0066FF] rounded-sm" /> Applications</div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" /> Funded</div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div className="text-sm font-semibold text-[#0A2540]">Applicants ({rows.length})</div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#0066FF]" />}
        </div>
        {rows.length === 0 && !loading ? (
          <div className="p-8 text-center text-sm text-[#6B7280]">
            No online applications in this range.<br />
            <span className="text-xs">Mark existing applicants as Online Lead from their applicant record to populate this list.</span>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="oa-table">
            <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Applicant</th>
                <th className="text-left px-4 py-2 font-medium">Application Date</th>
                <th className="text-left px-4 py-2 font-medium">Funding Date</th>
                <th className="text-left px-4 py-2 font-medium">State</th>
                <th className="text-right px-4 py-2 font-medium">Requested</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Funded</th>
                <th className="text-left px-4 py-2 font-medium">Attorney / Firm</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]"
                    data-testid={`oa-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/admin/applications/${r.id}`}
                      className="text-[#0066FF] hover:underline font-medium"
                      data-testid={`oa-row-${r.id}-name`}
                    >{r.applicant_name || "(no name)"}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-[#6B7280] font-mono text-xs">{r.application_date}</td>
                  <td className="px-4 py-2.5 text-[#6B7280] font-mono text-xs" data-testid={`oa-row-${r.id}-funding-date`}>{r.funding_date || "—"}</td>
                  <td className="px-4 py-2.5 text-[#0A2540]">{r.state || "—"}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] font-medium">{fmtUSD(r.amount_requested)}</td>
                  <td className="px-4 py-2.5">
                    <span className={"inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium border " + (STATUS_COLORS[r.status] || "bg-gray-50 text-gray-700 border-gray-200")}>
                      {(r.status || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-emerald-700 font-medium">{r.amount_funded ? fmtUSD(r.amount_funded) : "—"}</td>
                  <td className="px-4 py-2.5 text-xs">
                    <div className="text-[#0A2540]">{r.attorney_name || "—"}</div>
                    <div className="text-[#6B7280]">{r.attorney_firm}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-4" data-testid={testid}>
      <div className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-bold text-[#0A2540] mt-1.5">{value}</div>
    </div>
  );
}
